import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Smartphone, Activity, Database, RefreshCw } from "lucide-react";
import { getSecurityOverview, runPrivacyCleanup } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CustomerLookup } from "@/components/CustomerLookup";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const Stat = ({ label, value, warn, testid }) => (
  <div className={`rounded-xl border p-4 ${warn ? "border-amber-400/40 bg-amber-500/10" : "border-white/10"}`} data-testid={testid}>
    <p className="overline text-white/50">{label}</p>
    <p className={`font-serif text-3xl mt-1 ${warn ? "text-amber-200" : "text-white"}`}>{value}</p>
  </div>
);

export const SecurityPanel = () => {
  const [o, setO] = useState(null);
  const load = () => getSecurityOverview().then(setO).catch(() => toast.error("Couldn't load security overview"));
  useEffect(() => { load(); }, []);
  if (!o) return <p className="text-white/50">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="security-panel">
      <div className="flex items-center gap-2">{o.failed_7d > 4 ? <ShieldAlert className="w-5 h-5 text-amber-300" /> : <ShieldCheck className="w-5 h-5 text-emerald-300" />}<p className="overline text-[#F5F5F0]/70">Security & privacy · last 7 days</p></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Admin logins" value={o.success_7d} testid="stat-logins" />
        <Stat label="Failed attempts" value={o.failed_7d} warn={o.failed_7d > 4} testid="stat-failed" />
        <Stat label="New devices" value={o.new_devices_7d} warn={o.new_devices_7d > 0} testid="stat-devices" />
        <Stat label="Customer records held" value={o.records_with_personal_data} testid="stat-records" />
      </div>
      <CustomerLookup onChanged={load} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-white/10 rounded-xl p-5" data-testid="security-logins">
          <p className="overline text-[#93C5FD] mb-3">Recent logins</p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto text-sm">
            {o.logins.length === 0 && <p className="text-white/40">No logins recorded yet.</p>}
            {o.logins.map((l, i) => (
              <div key={i} className="flex items-start justify-between gap-3 border-b border-white/5 pb-1.5">
                <div className="min-w-0"><p className={l.ok ? "text-white/85" : "text-amber-200"}>{l.ok ? "Signed in" : "Failed attempt"} · {l.email}</p><p className="text-xs text-white/40 font-mono truncate">{l.ip} · {l.ua}</p></div>
                <span className="text-xs text-white/50 font-mono shrink-0">{when(l.at)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 rounded-xl p-5" data-testid="security-devices">
          <p className="overline text-[#93C5FD] mb-3 flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Devices that have used Admin</p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto text-sm">
            {o.devices.map((d, i) => (
              <div key={i} className="border-b border-white/5 pb-1.5"><p className="text-white/85 truncate">{d.ua || "Unknown device"}</p><p className="text-xs text-white/40 font-mono">first {when(d.first_seen)} · last {when(d.last_seen)} · {d.ip}</p></div>
            ))}
            {o.devices.length === 0 && <p className="text-white/40">None yet.</p>}
          </div>
          <p className="text-xs text-white/40 mt-3">Don't recognise one? Change the Admin password (Emergent → Secrets → ADMIN_PASSWORD) and follow the breach checklist in the Staff guide.</p>
        </div>
      </div>
      <div className="border border-white/10 rounded-xl p-5" data-testid="security-retention">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="overline text-[#93C5FD] flex items-center gap-1"><Database className="w-3.5 h-3.5" /> Personal data retention</p>
            <p className="text-sm text-white/70 mt-1">Names, emails, phones and waiver names are anonymised automatically <strong className="text-white">{o.retention_days} days</strong> after the hire date (runs nightly at 3am). {o.anonymised_records} bookings anonymised so far · oldest personal record: {o.oldest_personal_record || "—"}{o.last_cleanup?.finished_at ? ` · last run ${when(o.last_cleanup.finished_at)}` : ""}.</p>
          </div>
          <Button data-testid="cleanup-now-btn" variant="outline" onClick={async () => { await runPrivacyCleanup(); toast.success("Cleanup started — refresh in a few seconds"); setTimeout(load, 3000); }} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"><RefreshCw className="w-4 h-4 mr-1" /> Run cleanup now</Button>
        </div>
      </div>
      <div className="border border-white/10 rounded-xl p-5" data-testid="security-activity">
        <p className="overline text-[#93C5FD] mb-3 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Activity timeline</p>
        <div className="space-y-1 max-h-96 overflow-y-auto text-sm">
          {o.activity.map((a, i) => (
            <div key={i} className="flex items-start justify-between gap-3 border-b border-white/5 pb-1"><p className="text-white/80">{a.what} <span className="text-white/40">· {a.by}</span></p><span className="text-xs text-white/50 font-mono shrink-0">{when(a.at)}</span></div>
          ))}
          {o.activity.length === 0 && <p className="text-white/40">Nothing yet.</p>}
        </div>
      </div>
    </div>
  );
};
